import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Plus, ArrowDown, ArrowUp, Package } from "lucide-react";
import { toast } from "sonner";

export default function MasterPartDetailModal({ masterId, onClose }) {
  const [data, setData] = useState(null);
  const [addQty, setAddQty] = useState("");
  const [addReason, setAddReason] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/master-parts/${masterId}`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [masterId]);

  const stockIn = async (e) => {
    e.preventDefault();
    const qty = parseInt(addQty, 10);
    if (!qty || qty <= 0) { toast.error("Cantidad inválida"); return; }
    try {
      await api.post(`/master-parts/${masterId}/stock-in`, { quantity: qty, reason: addReason || "Entrada manual" });
      toast.success(`+${qty} unidades al stock del maestro`);
      setAddQty(""); setAddReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) return null;
  const { master, linked_parts = [], movements = [] } = data;
  const low = (master.stock ?? 0) <= (master.min_stock ?? 0);
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-auto ds-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0] sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Artículo maestro</div>
            <div className="font-display font-bold text-xl">{master.name}</div>
            <div className="font-mono text-xs text-[#525252]">{master.sku}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F7F7F4] rounded" data-testid="master-detail-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Stock + Entrada rápida */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="ds-card p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Stock actual</div>
              <div className={`font-display text-4xl font-black mt-1 ${low ? "text-red-700" : ""}`} data-testid="master-detail-stock">
                {master.stock ?? 0}
              </div>
              <div className="text-xs text-[#525252]">Mínimo: {master.min_stock ?? 0}</div>
              {master.notes && (
                <div className="mt-3 text-xs text-[#525252] italic">{master.notes}</div>
              )}
            </div>
            <form onSubmit={stockIn} className="ds-card p-4 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Entrada de stock</div>
              <div className="flex gap-2">
                <input
                  type="number" min="1" value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                  placeholder="Cantidad"
                  data-testid="master-stockin-qty"
                  className="w-24 ds-card px-3 py-2 text-sm"
                />
                <input
                  type="text" value={addReason}
                  onChange={(e) => setAddReason(e.target.value)}
                  placeholder="Motivo (proveedor, ajuste...)"
                  data-testid="master-stockin-reason"
                  className="flex-1 ds-card px-3 py-2 text-sm"
                />
              </div>
              <button type="submit" data-testid="master-stockin-submit" className="w-full ds-btn primary justify-center py-2">
                <Plus size={14} /> Añadir al stock
              </button>
            </form>
          </div>

          {/* Artículos vinculados */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
              Artículos que utilizan este stock ({linked_parts.length})
            </div>
            <div className="ds-card divide-y divide-[#E5E5E0]">
              {linked_parts.map((p) => (
                <div key={p.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`master-detail-linked-${p.id}`}>
                  <Package size={14} className="text-[#525252]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                      {p.sku} · {p.category || "—"}
                    </div>
                  </div>
                  <div className="font-mono text-sm">€{Number(p.sale_price || 0).toFixed(2)}</div>
                </div>
              ))}
              {linked_parts.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-[#525252]">
                  Ningún artículo comercial está vinculado a este maestro.
                </div>
              )}
            </div>
          </div>

          {/* Movimientos */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
              Historial de movimientos ({movements.length})
            </div>
            <div className="ds-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2 text-right">Δ</th>
                    <th className="px-3 py-2">Motivo</th>
                    <th className="px-3 py-2">Artículo / Reparación</th>
                    <th className="px-3 py-2">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} className="border-b border-[#E5E5E0] last:border-0" data-testid={`master-mov-${m.id}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#525252]">{fmtDate(m.created_at)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${m.delta < 0 ? "text-red-700" : "text-[#6E8B3D]"}`}>
                        {m.delta > 0 ? "+" : ""}{m.delta}
                        <span className="ml-1 text-[10px] text-[#525252] font-normal">
                          {m.delta < 0 ? <ArrowDown size={10} className="inline" /> : <ArrowUp size={10} className="inline" />}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{m.reason}</td>
                      <td className="px-3 py-2 text-xs">
                        {m.repair_code && <span className="font-mono">{m.repair_code}</span>}
                        {m.repair_code && m.part_name && <span className="text-[#525252]"> · </span>}
                        {m.part_name && <span>{m.part_name}</span>}
                        {!m.repair_code && !m.part_name && <span className="text-[#525252]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#525252]">{m.user_name || "—"}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-[#525252]">Aún no hay movimientos.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
